import { useReadContracts } from 'wagmi'
import { P2P_ESCROW_ABI } from '../lib/p2p/abi'
import { escrowAddressForChain } from '../lib/p2p/tokens'

const UNI2_CHAIN_ID = 111888
const BSC_CHAIN_ID = 56

export interface P2PChainFees {
  platformFeeBps: number
  gasFeeFlatWei: bigint
}

export interface P2PPlatformFees {
  uni2: P2PChainFees | null
  bsc: P2PChainFees | null
  isLoading: boolean
}

/**
 * Reads `platformFeeBps` + `gasFeeFlat` from both P2PEscrow V2 contracts so
 * the user-facing P2P page can show what the platform takes per trade.
 * Values are governed on-chain — admin updates are reflected on next refetch.
 */
export function useP2PPlatformFees(): P2PPlatformFees {
  const uni2Addr = escrowAddressForChain('UNI2')
  const bscAddr = escrowAddressForChain('BSC')

  const { data, isLoading } = useReadContracts({
    contracts: [
      { address: uni2Addr, abi: P2P_ESCROW_ABI, functionName: 'platformFeeBps', chainId: UNI2_CHAIN_ID },
      { address: uni2Addr, abi: P2P_ESCROW_ABI, functionName: 'gasFeeFlat', chainId: UNI2_CHAIN_ID },
      { address: bscAddr, abi: P2P_ESCROW_ABI, functionName: 'platformFeeBps', chainId: BSC_CHAIN_ID },
      { address: bscAddr, abi: P2P_ESCROW_ABI, functionName: 'gasFeeFlat', chainId: BSC_CHAIN_ID },
    ],
    query: { staleTime: 60_000 },
  })

  const parse = (bpsRes?: { status: string; result?: unknown }, flatRes?: { status: string; result?: unknown }): P2PChainFees | null => {
    if (!bpsRes || !flatRes || bpsRes.status !== 'success' || flatRes.status !== 'success') return null
    return {
      platformFeeBps: Number(bpsRes.result as bigint | number),
      gasFeeFlatWei: BigInt(flatRes.result as bigint | number | string),
    }
  }

  return {
    uni2: parse(data?.[0], data?.[1]),
    bsc: parse(data?.[2], data?.[3]),
    isLoading,
  }
}
